/**
 * Centralised render-quality tiering for the game canvases
 * (Pulse / Circles / Piano / Guitar / Waves). All renderers detect
 * once at construction and consult this module's flags whenever they
 * need to decide between the polished desktop look and the
 * stripped-down mobile path.
 *
 * The motivation is concrete: midrange phones (Pixel-a-tier hardware,
 * DPR 2.5–3, mid-spec GPUs) drop frames on the canvas-heavy parts of
 * the renderer — particle shadows, full-viewport radial bloom,
 * per-frame gradient allocation, and the raw fill-rate cost of a
 * DPR-scaled backing store. Reducing those targeted things ~halves
 * the per-frame GPU time without visibly degrading the experience.
 *
 * Detection runs once: a coarse pointer (`pointer: coarse` media
 * query) is the most reliable mobile signal, with a small viewport
 * (`max-width: 720px`) as a fallback for tablets-in-landscape or
 * stylus devices that report fine pointers. Both signals together
 * mean "treat this as touch / phone class hardware."
 *
 * The tier is captured at module load time rather than re-evaluated
 * per frame. A user who rotates their phone or attaches a mouse mid-
 * run keeps whatever class was detected at session start — the
 * alternative (a live `matchMedia` listener piped through every
 * renderer) is more code for a vanishingly rare case. Re-entering
 * the play page picks up the new class.
 */

export type RenderQualityTier = 'high' | 'low';

export interface RenderQuality {
  /**
   * Coarse "is this device touch-class?" verdict. Drives every flag
   * below. Callers usually shouldn't branch on this directly —
   * branch on the specific flag instead so the policy stays in one
   * place.
   */
  tier: RenderQualityTier;
  /**
   * Cap applied to `devicePixelRatio` when sizing the canvas backing
   * store. Native DPR can be 3 on phones, so an uncapped backing
   * store is 9× the fill-rate of a DPR-1 render — the single biggest
   * mobile cost. 1.5 keeps text crisp without paying the 3× tax.
   */
  dprCap: number;
  /**
   * Multiplier applied to particle emission counts (per-burst
   * spawns). 0.5 keeps the visual vocabulary but halves how many
   * triangles the GPU has to composite each frame.
   */
  particleScale: number;
  /**
   * Whether per-particle `shadowBlur` is allowed in the particle
   * draw loop. Canvas shadows are GPU-expensive on mobile
   * compositors; under 600 alive particles the per-particle blur
   * dominates frame time.
   */
  particleShadows: boolean;
  /**
   * Whether full-viewport "bloom" overlays (radial gradients drawn
   * over the entire canvas every frame) should be skipped. Reduces
   * background work to a single linear gradient fill.
   */
  backgroundBloom: boolean;
  /**
   * Whether non-essential playfield decoration layers should draw:
   * Pulse's track grid + parallax star field, and Circles' playfield
   * grid + parallax stars. Mostly a fill-rate / per-element-loop
   * win. The bg gradient + outer border still frame the playfield
   * clearly without these.
   */
  decorations: boolean;
  /**
   * Whether non-particle renderers should use `shadowBlur` for their
   * glow. Covers HUD chrome (score, combo, HP bar), gameplay FX
   * (slider body, slider ball, reverse arrows, shockwaves, hit
   * bursts, score pops, spinner rings, floating text, countdown),
   * and the cursor reticle.
   *
   * `shadowBlur > 0` is the single most expensive Canvas2D op on
   * mobile compositors — each one triggers a separate Gaussian-blur
   * pass over the affected pixels, and a busy circles frame can
   * stack 60+ shadow strokes across visible sliders, shockwaves,
   * bursts, and pops. False on low tier — callers must fall back to
   * flat strokes/fills and a manual `shadowBlur = 0` reset after any
   * draw that touched it (the reset is cheap; the assignment with a
   * positive blur is what costs).
   */
  chromeShadows: boolean;
  /**
   * Whether the per-tile shadow / inner-glow passes in the piano
   * renderer are allowed. Each tile fires multiple shadowBlur strokes
   * (head, ribbon body, dual-note accents) and a busy chart has 20+
   * tiles on screen — by far the dominant per-frame cost on phones.
   */
  tileShadows: boolean;
}

let cached: RenderQuality | null = null;

/**
 * Resolve the render-quality tier for this session. Cached — the
 * detection only runs once per page lifetime.
 *
 * Exposed as a function (not a constant) so test code can call
 * {@link resetRenderQualityForTesting} between runs to exercise both
 * tiers without restarting the worker.
 */
export function getRenderQuality(): RenderQuality {
  if (cached) {
    return cached;
  }
  cached = detect();
  return cached;
}

/**
 * Test-only: drop the cached detection so the next
 * {@link getRenderQuality} call re-evaluates. Production callers
 * should never need this — the tier is intentionally session-stable.
 */
export function resetRenderQualityForTesting(): void {
  cached = null;
}

function detect(): RenderQuality {
  if (typeof window === 'undefined') {
    // SSR / headless: assume desktop. The screenshot harness wants
    // the full polish so visual diffs catch regressions.
    return highTier();
  }

  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const narrowViewport = window.innerWidth <= 720;
  const lowCoreCount = (navigator.hardwareConcurrency ?? 8) <= 4;

  // Any of: a touch primary pointer, a phone-class viewport, or a
  // low-core-count CPU pulls us into the low tier. The OR is
  // deliberately generous — a midrange laptop with 4 cores would
  // also benefit from the lighter pipeline.
  if (coarsePointer || narrowViewport || lowCoreCount) {
    return lowTier();
  }
  return highTier();
}

function highTier(): RenderQuality {
  return {
    tier: 'high',
    dprCap: 2,
    particleScale: 1,
    particleShadows: true,
    backgroundBloom: true,
    decorations: true,
    chromeShadows: true,
    tileShadows: true,
  };
}

function lowTier(): RenderQuality {
  return {
    tier: 'low',
    // 1.5 is the sweet spot — text and lines stay clearly antialiased
    // on a DPR-3 phone, but fill-rate is ~4× lower than uncapped.
    dprCap: 1.5,
    particleScale: 0.5,
    particleShadows: false,
    backgroundBloom: false,
    decorations: false,
    chromeShadows: false,
    tileShadows: false,
  };
}
